"use client";

import { FC, PropsWithChildren, useContext, useEffect, useRef, useState } from "react";
import {
    AuthContext,
    WebsocketProvider as FeWebsocketProvider,
    useRuntimeConfig,
} from "@finbackoffice/site-core";
import { Language } from "@finbackoffice/enums";
import { WebsocketClient } from "@finbackoffice/websocket-client";

type IWebsocketState = {
    connected: boolean;
};

export const WebsocketProvider: FC<
    PropsWithChildren & { clientWsEndpoint: string; locale: string }
> = ({ children, clientWsEndpoint, locale }) => {
    const VERSION = useRuntimeConfig("VERSION");
    const { userToken } = useContext(AuthContext);
    const socketClientRef = useRef<WebsocketClient | null>(null);

    const [config, setConfig] = useState<IWebsocketState>({ connected: false });

    useEffect(() => {
        if (typeof window === "undefined") return;

        // Clean up old client if it exists
        if (socketClientRef.current) {
            socketClientRef.current.destroy();
        }

        const socketClient = new WebsocketClient(clientWsEndpoint, locale as Language);

        socketClientRef.current = socketClient;

        const handleConnected = () => {
            setConfig({ connected: true });
        };

        const handleDisconnected = () => {
            setConfig({ connected: false });
        };

        socketClient.on("connected", handleConnected);
        socketClient.on("disconnected", handleDisconnected);

        if (VERSION === "local") {
            (window as any).socketClient = socketClient;
        }

        return () => {
            socketClient.off("connected", handleConnected);
            socketClient.off("disconnected", handleDisconnected);
            socketClient.destroy();
        };
    }, [clientWsEndpoint, locale, VERSION]);

    return (
        <FeWebsocketProvider
            socketClient={socketClientRef.current}
            token={userToken || ""}
            connected={config.connected}>
            {children}
        </FeWebsocketProvider>
    );
};
